import {
    useSuspenseQuery,
    UseSuspenseQueryResult,
    useMutation,
    UseMutationResult,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import {
    dashboardFiltersResponseSchema,
    dashboardSettingsResponseSchema,
    overviewTabsConfigResponseSchema,
    saveSettingsResponseSchema,
    checkSettingsAvailabilityResponseSchema,
    locationsByProjectsResponseSchema,
    overviewTabResponseSchema,
    DashboardFiltersResponse,
    DashboardSettingsResponse,
    DashboardSetting,
    OverviewTabsConfigResponse,
    UpdateTabsConfigRequest,
    SetOverviewTabRequest,
    SaveSettingsResponse,
    CheckSettingsAvailabilityResponse,
    LocationsByProjectsResponse,
    OverviewTabResponse,
    OverviewLocationFilters,
    locationBreakdownResponseSchema,
    LocationBreakdownResponse,
} from './companyDashboard.types';

export type {
    DashboardFiltersResponse,
    DashboardSettingsResponse,
    DashboardSetting,
    OverviewTabsConfigResponse,
    AvailableProject,
    OverviewTabConfig,
    UpdateTabsConfigRequest,
    SetOverviewTabRequest,
    SaveSettingsResponse,
    CheckSettingsAvailabilityResponse,
    LocationsByProjectsResponse,
    OverviewTabResponse,
    LocationSummary,
    PieChartDatum,
    OverviewLocationFilters,
    LocationBreakdownResponse,
    LocationBreakdownRow,
} from './companyDashboard.types';

// ---- Query keys ----

export const DASHBOARD_FILTERS_QUERY_KEY = 'CompanyDashboardFilters';
export const DASHBOARD_SETTINGS_QUERY_KEY = 'CompanyDashboardSettings';
export const OVERVIEW_TABS_CONFIG_QUERY_KEY = 'CompanyDashboardOverviewTabsConfig';
export const LOCATIONS_BY_PROJECTS_QUERY_KEY = 'CompanyDashboardLocationsByProjects';
export const OVERVIEW_TAB_QUERY_KEY = 'CompanyDashboardOverviewTab';
export const LOCATION_BREAKDOWN_QUERY_KEY = 'CompanyDashboardLocationBreakdown';

// ---- Dashboard Filters ----

const loadDashboardFilters = async (): Promise<DashboardFiltersResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/filters`)
        .json();
    const parsed = dashboardFiltersResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadDashboardFilters: response did not match schema', parsed.error);
        return dashboardFiltersResponseSchema.parse({});
    }
    return parsed.data;
};

export const useLoadDashboardFilters = (): UseSuspenseQueryResult<DashboardFiltersResponse> =>
    useSuspenseQuery<DashboardFiltersResponse>({
        queryKey: [DASHBOARD_FILTERS_QUERY_KEY],
        queryFn: loadDashboardFilters,
    });

// ---- Dashboard Settings ----

const loadDashboardSettings = async (): Promise<DashboardSettingsResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/settings`)
        .json();
    const parsed = dashboardSettingsResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadDashboardSettings: response did not match schema', parsed.error);
        return [];
    }
    return parsed.data;
};

export const useLoadDashboardSettings = (): UseSuspenseQueryResult<DashboardSettingsResponse> =>
    useSuspenseQuery<DashboardSettingsResponse>({
        queryKey: [DASHBOARD_SETTINGS_QUERY_KEY],
        queryFn: loadDashboardSettings,
    });

// ---- Overview Tabs Config ----

const getOverviewTabsConfig = async (): Promise<OverviewTabsConfigResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-tabs-config`)
        .json();
    const parsed = overviewTabsConfigResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getOverviewTabsConfig: response did not match schema', parsed.error);
        return overviewTabsConfigResponseSchema.parse({});
    }
    return parsed.data;
};

export const useGetOverviewTabsConfig = (): UseSuspenseQueryResult<OverviewTabsConfigResponse> =>
    useSuspenseQuery<OverviewTabsConfigResponse>({
        queryKey: [OVERVIEW_TABS_CONFIG_QUERY_KEY],
        queryFn: getOverviewTabsConfig,
    });

// ---- Mutations ----

// Save settings (POST — creates new)
const saveSettings = async (settings: DashboardSetting): Promise<SaveSettingsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/settings`, { json: settings })
        .json();
    const parsed = saveSettingsResponseSchema.safeParse(response);
    return parsed.success ? parsed.data : (response as SaveSettingsResponse);
};

export const useSaveSettings = (
    onSuccess?: (data: SaveSettingsResponse) => void
): UseMutationResult<SaveSettingsResponse, Error, DashboardSetting> => {
    const queryClient = useQueryClient();
    return useMutation<SaveSettingsResponse, Error, DashboardSetting>({
        mutationFn: saveSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [DASHBOARD_SETTINGS_QUERY_KEY] });
            onSuccess?.(data);
        },
    });
};

// Update settings (PUT — updates existing by id)
const updateSettings = async ({ settings, id }: { settings: DashboardSetting; id: string }): Promise<SaveSettingsResponse> => {
    const response = await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/settings/${id}`, { json: settings })
        .json();
    const parsed = saveSettingsResponseSchema.safeParse(response);
    return parsed.success ? parsed.data : (response as SaveSettingsResponse);
};

export const useUpdateSettings = (
    onSuccess?: (data: SaveSettingsResponse) => void
): UseMutationResult<SaveSettingsResponse, Error, { settings: DashboardSetting; id: string }> => {
    const queryClient = useQueryClient();
    return useMutation<SaveSettingsResponse, Error, { settings: DashboardSetting; id: string }>({
        mutationFn: updateSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [DASHBOARD_SETTINGS_QUERY_KEY] });
            onSuccess?.(data);
        },
    });
};

// Check settings availability
const checkSettingsAvailability = async (settings: DashboardSetting): Promise<CheckSettingsAvailabilityResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/availability`, { json: settings })
        .json();
    const parsed = checkSettingsAvailabilityResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('checkSettingsAvailability: response did not match schema', parsed.error);
        return [];
    }
    return parsed.data;
};

export const useCheckSettingsAvailability = (): UseMutationResult<CheckSettingsAvailabilityResponse, Error, DashboardSetting> =>
    useMutation<CheckSettingsAvailabilityResponse, Error, DashboardSetting>({
        mutationFn: checkSettingsAvailability,
    });

// Update overview tabs config
const updateOverviewTabsConfig = async (config: UpdateTabsConfigRequest): Promise<void> => {
    await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/overview-tabs-config`, { json: config })
        .json();
};

export const useUpdateOverviewTabsConfig = (
    onSuccess?: () => void
): UseMutationResult<void, Error, UpdateTabsConfigRequest> => {
    const queryClient = useQueryClient();
    return useMutation<void, Error, UpdateTabsConfigRequest>({
        mutationFn: updateOverviewTabsConfig,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [OVERVIEW_TABS_CONFIG_QUERY_KEY] });
            onSuccess?.();
        },
    });
};

// Set active overview tab (fire-and-forget, no invalidation needed)
const setOverviewTab = async (request: SetOverviewTabRequest): Promise<void> => {
    await createPacerApi()
        .put(`${API_ROOT}v2/company-dashboard/set-overview-tab`, { json: request })
        .json();
};

export const useSetOverviewTab = (): UseMutationResult<void, Error, SetOverviewTabRequest> =>
    useMutation<void, Error, SetOverviewTabRequest>({
        mutationFn: setOverviewTab,
    });

// ---- Helper: build URLSearchParams from filters (handles arrays as repeated keys) ----

const buildSearchParams = (filters: OverviewLocationFilters): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null) continue;
        // Booleans are always sent explicitly (including `false`) so this GET stays consistent
        // with the sibling POST, which serializes the same filters object as JSON.
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
        } else {
            params.set(key, String(value));
        }
    }
    return params;
};

// ---- Helper: deterministic serialization of filters for query keys (sorted id arrays) ----

const serializeFilters = (filters: OverviewLocationFilters): string => {
    const normalized: Record<string, number[] | boolean> = {};
    for (const [key, value] of Object.entries(filters)) {
        normalized[key] = Array.isArray(value) ? [...value].sort((a, b) => a - b) : value;
    }
    return JSON.stringify(normalized, Object.keys(normalized).sort());
};

// ---- Load Locations By Projects ----

const EMPTY_LOCATIONS_BY_PROJECTS: LocationsByProjectsResponse = { brand: [], area: [], region: [], type: [], managementCompany: [] };

const loadLocationsByProjects = async (filters: OverviewLocationFilters): Promise<LocationsByProjectsResponse> => {
    const response = await createPacerApi()
        .post(`${API_ROOT}v2/company-dashboard/load-locations-by-project`, { json: filters })
        .json();
    const parsed = locationsByProjectsResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('loadLocationsByProjects: response did not match schema', parsed.error);
        return EMPTY_LOCATIONS_BY_PROJECTS;
    }
    return parsed.data;
};

export const useLoadLocationsByProjects = (
    filters: OverviewLocationFilters
): UseQueryResult<LocationsByProjectsResponse> =>
    useQuery<LocationsByProjectsResponse>({
        queryKey: [LOCATIONS_BY_PROJECTS_QUERY_KEY, serializeFilters(filters)],
        queryFn: () => loadLocationsByProjects(filters),
        enabled: filters.projectIds.length > 0,
        initialData: EMPTY_LOCATIONS_BY_PROJECTS,
    });

// ---- Get Overview Tab ----

const getOverviewTab = async (id: number, filters: OverviewLocationFilters): Promise<OverviewTabResponse> => {
    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-tab/${id}`, {
            searchParams: buildSearchParams(filters),
        })
        .json();
    const parsed = overviewTabResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getOverviewTab: response did not match schema', parsed.error);
        return { locations: [] };
    }
    return parsed.data;
};

export const useGetOverviewTab = (id: number, filters: OverviewLocationFilters): UseQueryResult<OverviewTabResponse> =>
    useQuery<OverviewTabResponse>({
        queryKey: [OVERVIEW_TAB_QUERY_KEY, id, serializeFilters(filters)],
        queryFn: () => getOverviewTab(id, filters),
        enabled: filters.projectIds.length > 0,
        // Deliberately no `initialData`: seeding it makes the query report `success` from the
        // first render, so `isPending` — and therefore `isLoading` — is false forever, leaving
        // callers with only `isFetching` (true on every background refetch) to drive a loading
        // state. Callers already tolerate `data` being undefined.
    });

// ---- Get Location Breakdown ----

const EMPTY_LOCATION_BREAKDOWN: LocationBreakdownResponse = { groupBy: '', groups: [] };

const getLocationBreakdown = async (
    locationKey: string,
    groupBy: string,
    filters: OverviewLocationFilters
): Promise<LocationBreakdownResponse> => {
    // Same flat filter params as overview-tab — the endpoint binds the identical FilterModel,
    // so the panel counts exactly the tasks the location row above it counts.
    const searchParams = buildSearchParams(filters);
    searchParams.set('locationKey', locationKey);
    searchParams.set('groupBy', groupBy);

    const response = await createPacerApi()
        .get(`${API_ROOT}v2/company-dashboard/overview-location-breakdown`, { searchParams })
        .json();
    const parsed = locationBreakdownResponseSchema.safeParse(response);
    if (!parsed.success) {
        console.warn('getLocationBreakdown: response did not match schema', parsed.error);
        return EMPTY_LOCATION_BREAKDOWN;
    }
    return parsed.data;
};

/**
 * One location's metrics broken down by department / discipline / phase / tag.
 *
 * `enabled` is what keeps the breakdown panel honest: it is passed the panel's expanded
 * state, so a panel that is animating shut (still mounted, no longer expanded) does not
 * fire a fetch, and a row that was never opened never costs a request.
 */
export const useGetLocationBreakdown = (
    locationKey: string | null,
    groupBy: string,
    filters: OverviewLocationFilters,
    enabled = true
): UseQueryResult<LocationBreakdownResponse> =>
    useQuery<LocationBreakdownResponse>({
        queryKey: [LOCATION_BREAKDOWN_QUERY_KEY, locationKey, groupBy, serializeFilters(filters)],
        queryFn: () => getLocationBreakdown(locationKey as string, groupBy, filters),
        enabled: enabled && !!locationKey,
    });
